import { useEffect, useState } from "react";

import { FileUpload } from "@/components/admin/FileUpload";
import { buildForcedDownloadUrl, getMusicMixDownloadFilename } from "@/lib/helpers";
import { Music, Download, Trash2 } from "lucide-react";

type Props = {
  playlistId: string;
  musicUrl: string | null;
  /** When provided, organizer controls (upload/replace/remove) are shown. */
  onMusicUrlChange?: (url: string | null) => Promise<string | null>;
  /** Extra className on the outer wrapper. */
  className?: string;
};

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export function MusicMixSection({ playlistId, musicUrl, onMusicUrlChange, className = "" }: Props) {
  const isOrganizer = !!onMusicUrlChange;
  const safeUrl = musicUrl && isSafeUrl(musicUrl) ? musicUrl : null;
  const downloadFilename = safeUrl
    ? getMusicMixDownloadFilename(safeUrl, `music-mix-${playlistId}.mp3`)
    : null;
  const downloadUrl = safeUrl && downloadFilename
    ? buildForcedDownloadUrl(safeUrl, downloadFilename)
    : null;
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [musicError, setMusicError] = useState<string | null>(null);

  useEffect(() => {
    setMusicError(null);
  }, [musicUrl]);

  const handleMusicChange = async (nextUrl: string | null) => {
    if (!onMusicUrlChange) return;
    setIsSaving(true);
    setMusicError(null);
    const error = await onMusicUrlChange(nextUrl);
    setIsSaving(false);
    if (error) {
      setMusicError(error);
    }
  };

  const handleDownload = async () => {
    if (!safeUrl || !downloadFilename || !downloadUrl || isDownloading) return;

    if (downloadUrl !== safeUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setIsDownloading(true);
    setMusicError(null);

    try {
      const response = await fetch(safeUrl);
      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      setMusicError("Could not download the music mix. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // On shared pages, hide entirely when there's no music
  if (!isOrganizer && !safeUrl) return null;

  return (
    <section className={className}>
      <h2 className="mb-2.5 flex items-center gap-2 font-[family-name:var(--font-display)] text-[clamp(1.1rem,0.95rem+0.5vw,1.35rem)] font-medium leading-[1.2] text-[#321b29]">
        <Music className="h-[1.1em] w-[1.1em] text-[#762a5c]" />
        Music Mix
      </h2>

      {safeUrl ? (
        <div className="rounded-[1rem] border border-[#e2e4e9] bg-white p-4">
          <audio controls preload="metadata" className="w-full" src={safeUrl}>
            Your browser does not support the audio element.
          </audio>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                void handleDownload();
              }}
              disabled={isDownloading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e4e9] bg-white px-3 py-1.5 text-xs font-medium text-[#525866] transition-colors hover:bg-gray-50 disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              {isDownloading ? "Downloading…" : "Download"}
            </button>

            {isOrganizer && (
              <>
                <FileUpload
                  folder="music-mixes"
                  accept="audio/mpeg,audio/mp3"
                  label="Replace"
                  disabled={isSaving}
                  onUploaded={(url) => {
                    void handleMusicChange(url);
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    void handleMusicChange(null);
                  }}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e4e9] bg-white px-3 py-1.5 text-xs font-medium text-[#ba4d4d] transition-colors hover:bg-red-50 disabled:opacity-60"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </>
            )}
          </div>
          {musicError && (
            <p className="mt-3 m-0 text-[0.8rem] font-medium text-[#ba4d4d]">
              {musicError}
            </p>
          )}
        </div>
      ) : isOrganizer ? (
        <div className="rounded-[1rem] border border-[#e2e4e9] bg-white p-4">
          <p className="mb-3 text-[0.88rem] text-[#70696f]">
            Upload an MP3 dance mix so your performers can access the music here.
          </p>
          <FileUpload
            folder="music-mixes"
            accept="audio/mpeg,audio/mp3"
            label="Upload Music Mix"
            disabled={isSaving}
            onUploaded={(url) => {
              void handleMusicChange(url);
            }}
          />
          {musicError && (
            <p className="mt-3 m-0 text-[0.8rem] font-medium text-[#ba4d4d]">
              {musicError}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
